"use client";

/**
 * Message Input Component
 *
 * Premium command center design with nested input field,
 * quick action buttons, agent mentions (@), and skill picker (/).
 * Includes text highlighting for both patterns.
 *
 * NOTE: The highlight overlay uses dangerouslySetInnerHTML with content
 * that is sanitized via generateHighlightedContent() -> sanitizeHtml()
 * which escapes all HTML special characters before inserting highlight spans.
 */

import {
  useState,
  useCallback,
  useRef,
  useEffect,
  KeyboardEvent,
  ChangeEvent,
  memo,
} from "react";
import type { ImageAttachment, FileAttachment } from "@bash/shared";
import { cn } from "@/lib/utils";
import type { AgentInfo, SkillInfo } from "./HighlightOverlay";
import { useAttachmentHandler, AttachmentStrip } from "./AttachmentHandler";
import { useComposerFileDrop } from "./useComposerFileDrop";
import { toWireImages } from "./wire-attachments";
import { MentionDropdown } from "./MentionDropdown";
import { SkillDropdown } from "./SkillDropdown";
import { QuickActions } from "./QuickActions";
import { StagedQueue } from "./StagedQueue";
import { TaskAccessLevelPicker } from "@/components/settings/TaskAccessLevelPicker";
// The model control — the composer's single engine control (2026-07-10 redesign:
// engines hidden, tier selector dropped; model choice + model menu live here).
// The legacy HarnessSelector re-export was deleted with the arms.
import { ModelSelector } from "@/components/ui/ModelSelector";
import { useTaskStore } from "@/stores/taskStore";
import { setComposerTyping } from "@/stores/deskSignals";
import { useSession } from "@/hooks/useSession";
import { useActiveSkillIndex } from "@/hooks/useActiveSkillIndex";
import type { PrefillMode } from "@/stores/composerDraftStore";
import { applyPrefill } from "./prefill";
import { useLaneRecipient } from "./useLaneRecipient";
import { useComputerToggle } from "./useComputerToggle";
import { ConnectionHints, useSendPause } from "./ConnectionHints";
import { expandSlashOnSubmit } from "./slash-submit";
import { useMentionSkillPickers } from "./useMentionSkillPickers";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { PlusIcon } from "@/components/icons/PlusIcon";
import { SendIcon } from "@/components/icons/SendIcon";
import { GitBranchIcon } from "@/components/icons/GitBranchIcon";
import { ZapIcon } from "@/components/icons/ZapIcon";
import { MonitorIcon } from "@/components/icons/MonitorIcon";

interface CancelledMessage {
  content?: string;
  images?: ImageAttachment[];
}

export interface MessageInputProps {
  onSend: (
    message: string,
    images?: ImageAttachment[],
    files?: FileAttachment[]
  ) => void;
  onCancel?: () => void;
  disabled?: boolean;
  isProcessing?: boolean;
  placeholder?: string;
  cancelledMessage?: CancelledMessage | null;
  onCancelledMessageRestored?: () => void;
  /** Pre-fill the input once (e.g. from "Create with Bash"). Consumed and cleared via onInitialValueConsumed. */
  initialValue?: string | null;
  onInitialValueConsumed?: () => void;
  agents?: AgentInfo[];
  skills?: SkillInfo[];
  showQuickActions?: boolean;
  onTabSwitch?: (direction: "next" | "prev") => void;
  /** When true, shows an "Upgrade" CTA instead of the disabled send button */
  showUpgrade?: boolean;
  onUpgrade?: () => void;
  /** Tooltip text shown on hover over the disabled send button */
  disabledReason?: string;
  /** Pre-fill the input with a specific text (used by suggestion chip in layout) */
  prefillText?: string | null;
  /** How the prefill merges with the composer's live text: replace (default) or append. */
  prefillMode?: PrefillMode;
  /** Called after prefill text has been consumed */
  onPrefillConsumed?: () => void;
  /** Called on first keystroke to pre-warm sandbox (fires once per idle cycle) */
  onPreWarm?: () => void;
  /**
   * Show the manual browser toggle button (computer icon) next to the
   * ModeSelector. Opt-in because MessageInput is also used in surfaces
   * (Routines) where the browser pane doesn't apply.
   */
  showBrowserToggle?: boolean;
}

function MessageInputImpl({
  onSend,
  onCancel,
  disabled = false,
  isProcessing = false,
  placeholder = "@ for agents / for skills",
  cancelledMessage,
  onCancelledMessageRestored,
  agents = [],
  skills = [],
  showQuickActions = false,
  onTabSwitch,
  initialValue,
  onInitialValueConsumed,
  showUpgrade = false,
  onUpgrade,
  disabledReason,
  prefillText,
  prefillMode = "replace",
  onPrefillConsumed,
  onPreWarm,
  showBrowserToggle = false,
}: MessageInputProps) {
  // Manual browser toggle (computer icon next to ModeSelector).
  // PER-TASK: opening in task A shows the panel only in task A's layout.
  // Sandbox session lifecycle is refcounted by panel-open task ids — the
  // backend close only fires when the LAST task closes its panel.
  // On the empty new-task page, clicking creates a real "Browser session"
  // task and immediately opens its panel — no pending state needed.
  const currentTaskId = useTaskStore((s) => s.selectedTaskId);
  const pendingNewTaskId = useTaskStore((s) => s.pendingNewTaskId);
  // The CREATED task, if any. A pending new-task placeholder (allocated below by
  // the Computer button so its panel has somewhere to mount) is NOT a created
  // task — treat it as "no task" for the active-task gates (the lane in view,
  // access level), else clicking Computer flips the composer into existing-task
  // mode for a task that was never created (founder-reported, 2026-07-14).
  const createdTaskId =
    currentTaskId && currentTaskId !== pendingNewTaskId ? currentTaskId : null;
  // The Computer button (useComputerToggle.ts): per task, honest about a
  // browser behind a closed pane, and the SAME open the door's control runs.
  const computer = useComputerToggle(currentTaskId);
  const isPanelOpenForTask = computer.open;
  // Slash-command resolution — expands bare /skill to /ability:skill before submit
  const { session } = useSession();
  const userId = session?.user?.id ?? null;
  const skillIndex = useActiveSkillIndex(userId);

  // Why a send is waiting (ConnectionHints.tsx): a deploy window with the
  // socket down pauses sends; a down health rail only says so.
  const { deployPaused, offline } = useSendPause();

  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Attachment management
  const {
    pendingAttachments,
    imageError,
    setImageError,
    handleImageSelect,
    handleFiles,
    openFilePicker,
    removeAttachment,
    resetAttachments,
    restoreImages,
  } = useAttachmentHandler();

  // File intake — drag-and-drop, paste, and the desktop native drop bridge — all
  // routed into the same attach lane as the + button.
  const { isDragOver, dragHandlers, onPaste } = useComposerFileDrop({
    handleFiles,
    setImageError,
    rootRef,
  });

  // Spec 2026-09-06 §3.7, D2 — the selection IS the recipient. There is no
  // sticky state of the composer's own any more: the chip on the bench, the
  // screen in the column and the box down here read ONE key, so the three can
  // never disagree about who a message is for. null = the lead (Bash).
  const { laneInView, laneHasNoInbox, laneRecipient, addressToLane } =
    useLaneRecipient(createdTaskId);

  // The @ and / pickers and the overlay's content (useMentionSkillPickers.ts).
  const pickers = useMentionSkillPickers({
    message,
    setMessage,
    textareaRef,
    agents,
    skills,
    createdTaskId,
    laneInView,
  });
  const scanPickers = pickers.scan;

  // Auto-focus the textarea on mount and on task change so the user can
  // start typing immediately after opening a new task (or switching tasks)
  // without clicking into the input. InputStation is persistent across
  // task changes, so a task-keyed effect — not a mount effect — is what
  // actually fires at the right time.
  const selectedTaskId = useTaskStore((s) => s.selectedTaskId);
  useEffect(() => {
    if (disabled) return;
    const active = document.activeElement;
    const isUserTypingElsewhere =
      active instanceof HTMLElement &&
      active !== textareaRef.current &&
      (active.tagName === "INPUT" ||
        active.tagName === "TEXTAREA" ||
        active.isContentEditable);
    if (isUserTypingElsewhere) return;
    const id = requestAnimationFrame(() => textareaRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [selectedTaskId, disabled]);

  useEffect(() => {
    if (cancelledMessage) {
      setMessage(cancelledMessage.content || "");
      if (cancelledMessage.images?.length) {
        restoreImages(cancelledMessage.images);
      }
      textareaRef.current?.focus();
      onCancelledMessageRestored?.();
    }
  }, [cancelledMessage, onCancelledMessageRestored, restoreImages]);

  // Pre-fill from external source (e.g. "Create with Bash" button)
  useEffect(() => {
    if (initialValue) {
      setMessage(initialValue);
      onInitialValueConsumed?.();
      // Focus and place cursor at the end
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(
            initialValue.length,
            initialValue.length
          );
        }
      }, 0);
    }
  }, [initialValue, onInitialValueConsumed]);

  // Pre-fill from an external trigger (suggestion chip, Studio actions). Append
  // mode merges onto the composer's LIVE text so "Set as ref" never clobbers a
  // typed note or an already-injected prefill; replace swaps it.
  useEffect(() => {
    if (prefillText) {
      setMessage((prev) => applyPrefill(prev, prefillText, prefillMode));
      onPrefillConsumed?.();
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          const end = textareaRef.current.value.length;
          textareaRef.current.setSelectionRange(end, end);
        }
      }, 0);
    }
  }, [prefillText, prefillMode, onPrefillConsumed]);

  // Sync highlight overlay scroll with textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    const highlight = highlightRef.current;
    if (!textarea || !highlight) return;

    const syncScroll = () => {
      highlight.scrollTop = textarea.scrollTop;
      highlight.scrollLeft = textarea.scrollLeft;
    };

    textarea.addEventListener("scroll", syncScroll);
    return () => textarea.removeEventListener("scroll", syncScroll);
  }, []);

  // Auto-resize textarea to fit content, up to half the VISIBLE viewport —
  // window.innerHeight is the layout viewport and ignores the mobile keyboard,
  // so a growing textarea could bury the send row behind it.
  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    // A measurement taken before layout is not a measurement: a tab opened
    // from an external link can hydrate while it still has no boxes, and
    // scrollHeight reads 0. Pinning that zero collapsed the composer to an
    // invisible, unclickable strip that only a reload undid, because the
    // effect's sole trigger is `message`, which cannot change without typing
    // into the box nobody can click. Leave the intrinsic one-row height.
    const contentHeight = textarea.scrollHeight;
    if (!contentHeight) return;
    const maxHeight =
      (window.visualViewport?.height ?? window.innerHeight) * 0.5;
    const newHeight = Math.min(contentHeight, maxHeight);
    textarea.style.height = `${newHeight}px`;
    if (highlightRef.current) {
      highlightRef.current.style.height = `${newHeight}px`;
    }
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [message, resizeTextarea]);

  // Level-triggered re-measure: the box itself changing size is the signal
  // that layout finally happened (tab shown, container laid out, width
  // re-wrapped), so the height converges on the truth without depending on
  // any one cause of the late layout.
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => resizeTextarea());
    observer.observe(textarea);
    return () => observer.disconnect();
  }, [resizeTextarea]);

  // The desk's "listening" (task-story/Desk.tsx): words in the box with the
  // box in hand. The composer is the only thing that knows, so it says so —
  // and takes it back the moment the draft is sent, emptied or abandoned, or
  // the face would keep listening to someone who has walked away.
  const setTyping = useCallback(
    (drafting: boolean) => {
      if (currentTaskId) setComposerTyping(currentTaskId, drafting);
    },
    [currentTaskId]
  );
  // Blur is not the only way a draft is abandoned, and on a task switch it does
  // not fire at all — this component stays mounted (the task layout exists so
  // it does) and re-focuses. Without this the outgoing task kept `typing`
  // forever, and a stale "listening" outranks even a live question: task A
  // would sit claiming Bash was hearing someone out on a screen nobody was on.
  // The cleanup closes over the task being LEFT, and also fires on unmount —
  // which is how a pending ask swapping in the interaction card clears it too.
  useEffect(
    () => () => {
      if (currentTaskId) setComposerTyping(currentTaskId, false);
    },
    [currentTaskId]
  );

  const handleTextChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      const cursorPos = e.target.selectionStart || 0;

      // Pre-warm sandbox on first keystroke (fires once per idle cycle)
      if (value.length > 0 && onPreWarm) {
        onPreWarm();
      }

      setMessage(value);
      // Before the pickers' scan: a draft that starts with @ or / is still a draft.
      setTyping(value.trim().length > 0);
      scanPickers(value, cursorPos);
    },
    [onPreWarm, setTyping, scanPickers]
  );

  const handleSend = useCallback(() => {
    const trimmed = message.trim();
    const completeAttachments = pendingAttachments.filter(
      (a) => a.status === "complete"
    );
    // Deploy pause: while an update window has the socket down, the draft
    // stays visibly in the input — never fired into a silent buffer.
    if (deployPaused) return;
    if (
      (trimmed || completeAttachments.length > 0) &&
      !disabled &&
      !laneHasNoInbox
    ) {
      // toWireImages carries each attachment's display filename onto the wire
      // so the server stores the friendly name, not its inline-<ts> mint.
      const images: ImageAttachment[] = toWireImages(completeAttachments);
      const files: FileAttachment[] = [];
      for (const a of completeAttachments) {
        if (!(a.isImage && a.imageData) && a.fileData) {
          files.push({
            filename: a.filename,
            url: a.fileData.url,
            mediaType: a.fileData.mediaType,
            sizeBytes: a.fileData.sizeBytes,
          });
        }
      }

      // Resolve bare /skill to /ability:skill before dispatch (only on submit, not on keystroke)
      let textToSend = expandSlashOnSubmit(trimmed, skillIndex);

      // R5 — with a lane in view the send is ALWAYS prefixed with its roster
      // handle, which is what `parseTeammateMention` matches. Any handle the
      // person typed themselves is stripped first, so the prefix never doubles
      // and a malformed leading `@x` in the body is words rather than routing.
      textToSend = addressToLane(textToSend);
      onSend(
        textToSend,
        images.length > 0 ? images : undefined,
        files.length > 0 ? files : undefined
      );
      setMessage("");
      setTyping(false);
      resetAttachments();
    }
  }, [
    message,
    pendingAttachments,
    disabled,
    deployPaused,
    onSend,
    resetAttachments,
    skillIndex,
    laneHasNoInbox,
    addressToLane,
    setTyping,
  ]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Tab" && onTabSwitch) {
        e.preventDefault();
        onTabSwitch(e.shiftKey ? "prev" : "next");
        return;
      }

      if (e.key === "Enter" && !e.shiftKey) {
        // A composing Enter is the IME's own key (it commits a pinyin, romaji
        // or Indic word): it never sends. Safari fires it after compositionend
        // with isComposing false, so the legacy 229 is read too (R-F15).
        if (e.nativeEvent.isComposing || e.keyCode === 229) return;
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend, onTabSwitch]
  );

  const completeCount = pendingAttachments.filter(
    (a) => a.status === "complete"
  ).length;
  const hasProcessing = pendingAttachments.some(
    (a) => a.status === "processing"
  );
  const canSend =
    (message.trim() || completeCount > 0) &&
    !disabled &&
    !laneHasNoInbox &&
    !hasProcessing &&
    !deployPaused;

  // Right-side action cluster (stop / queue / send / upgrade). One definition,
  // rendered in the desktop toolbar OR the mobile row — never both, so every
  // aria-label stays unique in the DOM.
  const sendCluster = (
    <div className="flex items-center gap-1.5">
      <ConnectionHints deployPaused={deployPaused} offline={offline} />
      {isProcessing ? (
        // The running task's two verbs share ONE squared control: Queue on the
        // left, a white stop square on the right, a hairline between, all in the
        // control's own ink (founder 08-31: "white only for the stop button").
        // The red stop button this replaces read as an alarm on a page that
        // never shouts.
        <div className="run-split" role="group" aria-label="Running task">
          <button
            onClick={canSend ? handleSend : undefined}
            disabled={!canSend}
            className={cn("run-split__queue h-9", !canSend && "is-idle")}
            title={
              canSend
                ? "Queue message"
                : disabledReason || "Type a message to queue"
            }
            aria-label={
              canSend
                ? "Queue message"
                : disabledReason || "Type a message to queue"
            }
          >
            <GitBranchIcon />
            <span>Queue</span>
          </button>
          {onCancel && (
            <>
              <span className="run-split__sep" aria-hidden="true" />
              <button
                onClick={onCancel}
                className="run-split__stop"
                title="Stop"
                aria-label="Stop generation"
              >
                <span className="run-split__square" aria-hidden="true" />
              </button>
            </>
          )}
        </div>
      ) : canSend ? (
        <button
          onClick={handleSend}
          className="w-9 h-9 flex items-center justify-center rounded-10 text-fg-default bg-transparent border-none cursor-pointer transition-all ease-(--ease-smooth) hover:bg-white/6 hover:transform-[scale(1.05)] transition-all duration-200"
          title="Send"
          aria-label="Send message"
        >
          <SendIcon className="w-4.5 stroke-current stroke-2" />
        </button>
      ) : showUpgrade ? (
        <button
          onClick={onUpgrade}
          className="flex items-center gap-1.5 px-4 py-2 rounded-16 text-3.5 font-semibold transition-all hover:opacity-90 active:scale-[0.97] shrink-0 bg-fg-default text-bg-base"
          title="Upgrade plan"
          aria-label="Upgrade plan"
        >
          <ZapIcon width={14} height={14} strokeWidth="2.5" />
          Upgrade
        </button>
      ) : (
        <span title={disabledReason || "Send"}>
          <button
            disabled
            className="w-9 h-9 flex items-center justify-center rounded-10 text-fg-faint opacity-40 bg-transparent border-none cursor-default"
            aria-label={disabledReason || "Send message"}
          >
            <SendIcon className="w-4.5 stroke-current stroke-2" />
          </button>
        </span>
      )}
    </div>
  );

  const commonInputStyles =
    "px-4 pt-3 pb-1 text-4 leading-normal [font-family:inherit]";

  return (
    <div
      className="relative"
      ref={rootRef}
      data-composer-bar
      onDragEnter={dragHandlers.onDragEnter}
      onDragLeave={dragHandlers.onDragLeave}
      onDragOver={dragHandlers.onDragOver}
      onDrop={dragHandlers.onDrop}
    >
      {/* Drop overlay */}
      {isDragOver && (
        <div className="absolute inset-0 z-20 rounded-20 flex items-center justify-center pointer-events-none bg-palette-orange/6 border-2 border-dashed border-palette-orange/40">
          <span className="text-3.5 font-medium text-palette-orange/80">
            Drop files to attach
          </span>
        </div>
      )}

      {/* Pending Attachments */}
      <AttachmentStrip
        pendingAttachments={pendingAttachments}
        imageError={imageError}
        onRemove={removeAttachment}
      />

      {/* Server-authoritative staged-message tray (K6 P5) — above the composer */}
      <StagedQueue />

      {/* Textarea area — full width on desktop and mobile; */}
      <div className="relative">
        {/* Highlight Overlay */}
        <div
          ref={highlightRef}
          className={cn(
            commonInputStyles,
            "highlight-overlay text-fg-default absolute inset-0 pointer-events-none overflow-hidden whitespace-pre-wrap break-words"
          )}
          aria-hidden="true"
          dangerouslySetInnerHTML={{
            __html: pickers.highlightedContent || "&nbsp;",
          }}
        />

        {/* min-h is the belt: one `leading-normal` line box, so no height
            written onto this element can ever collapse the field out of
            reach of a click. */}
        <textarea
          ref={textareaRef}
          value={message}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setTyping(message.trim().length > 0)}
          onBlur={() => setTyping(false)}
          onPaste={onPaste}
          disabled={laneHasNoInbox}
          /* The box names the lane only while it can take a message: a shut
             box reading "Message <title>" is a lie, and whatever the layout
             is holding it shut for (the lane's own ask, no credits) is the
             thing the person needs to read instead. */
          placeholder={
            laneHasNoInbox
              ? "This helper doesn't take messages. Tell Bash."
              : laneRecipient && !disabled
                ? `Message ${laneRecipient.title}`
                : placeholder
          }
          rows={1}
          className={cn(
            commonInputStyles,
            "w-full bg-transparent resize-none focus:outline-none min-h-[1.5em] placeholder:text-fg-faint relative z-10 text-transparent [caret-color:var(--fg-default)] overflow-y-auto max-h-[50vh]"
          )}
        />

        {/* @mention Dropdown */}
        <MentionDropdown
          open={pickers.mention.open}
          agents={pickers.mention.agents}
          anchorRef={textareaRef}
          triggerPos={pickers.mention.triggerPos}
          onSelect={pickers.mention.onSelect}
          onClose={pickers.mention.onClose}
        />

        {/* /skill Dropdown */}
        <SkillDropdown
          open={pickers.skill.open}
          skills={pickers.skill.skills}
          anchorRef={textareaRef}
          triggerPos={pickers.skill.triggerPos}
          onSelect={pickers.skill.onSelect}
          onClose={pickers.skill.onClose}
        />
      </div>

      {/* Bottom toolbar — [+] [Mode ▾] ... [send/cancel] — desktop only; the
          mobile pill row above carries [+] and send, the sheet the rest. */}
      <div className="flex items-center justify-between px-3 pb-2 pt-2 relative z-10">
        <div className="flex items-center gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-md"
                  type="button"
                  aria-label="Attach file"
                  onClick={() => openFilePicker(handleImageSelect)}
                >
                  <PlusIcon className="w-4.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={6}>
                Attach file
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {/* Computer panel open = the composer is squeezed to a half-width
              column: the model + access pills drop to icon-only so the toolbar
              never overflows (founder-reported, 2026-07-15). Full pills return
              when the panel closes. */}
          <ModelSelector compact={isPanelOpenForTask} />
          {/* Always shown — on the new-task composer (no created task) it binds
              to the composer draft; once a task exists it shows that task's
              level. A pending placeholder counts as new-task (draft). */}
          <TaskAccessLevelPicker
            taskId={createdTaskId}
            compact={isPanelOpenForTask}
          />
          {showBrowserToggle && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={isPanelOpenForTask ? "default" : "ghost"}
                    size="icon-md"
                    type="button"
                    aria-label={
                      isPanelOpenForTask ? "Close Computer" : "Open Computer"
                    }
                    aria-pressed={isPanelOpenForTask}
                    className="relative"
                    onClick={computer.toggle}
                  >
                    <MonitorIcon className="w-4.5" />
                    {/* Browsing dot — a live/waking browser behind a closed panel.
                  var(--color-purple) is the "Browsing" status color. */}
                    {computer.showDot && (
                      <span
                        aria-hidden="true"
                        data-testid="composer-browser-dot"
                        className="absolute top-1 right-1 rounded-full w-[6px] h-[6px] bg-palette-purple"
                      />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={6}>
                  {isPanelOpenForTask ? "Close Computer" : "Open Computer"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {sendCluster}
      </div>

      {/* Quick Actions */}
      {showQuickActions && <QuickActions onSend={onSend} />}
    </div>
  );
}

// Memoized so an unrelated re-render of a parent (e.g. the task layout on a
// turn boundary) doesn't re-render the whole composer. Most props are stable
// values or memoized in the callers; string/boolean props compare by value.
export const MessageInput = memo(MessageInputImpl);
MessageInput.displayName = "MessageInput";
